// Proposal cards. A Copilot answer can PROPOSE; only these buttons, pressed by
// a person, write anything. Each one calls an ordinary authenticated endpoint
// that re-checks everything server-side.

import { useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Check, Copy, FileSearch, Mail, Radar, ShieldAlert, Trash2, X } from 'lucide-react';
import { safeExternalHref, type Citation, type Proposal, type WebRecipient } from '@shared/copilot/messages';
import { ATTENTION_LABEL, ATTENTION_STATES, firstName, type AttentionState } from '@shared/copilot/attention';
import { DRAFT_KIND_LABEL, isSupplierKind, webRecipientsToConfirm } from '@shared/copilot/drafts';
import { RELATIONSHIP_LABEL } from '@shared/copilot/candidates';
import type { ApiError } from '@/lib/api';
import {
  createGmailDraft, editDraft, getMailboxes, getTeamMembers, listCandidates, listHandoffs, recordCandidateFromMessage, recordHandoff,
  reviewCandidate, runPartsBaseSearch, saveDrafts, discardDraft, type CandidateView, type DraftView,
} from '@/lib/copilot-api';

const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimary = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';
const field = 'w-full min-w-0 text-sm border border-border rounded-md px-2 py-1.5 bg-background';
const errText = (e: unknown) => (e as ApiError | null)?.message ?? 'Something went wrong.';

function Card({ icon, title, children, tone = 'default' }: { icon: React.ReactNode; title: string; children: React.ReactNode; tone?: 'default' | 'warn' }) {
  return (
    <div className={`mt-2 rounded-lg border ${tone === 'warn' ? 'border-warning/50' : 'border-border'} bg-background p-3 text-sm space-y-2 min-w-0`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-muted">{icon}{title}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
export function HandoffCard({ proposal, messageId, rfqPublicId }: { proposal: Extract<Proposal, { type: 'handoff' }>; messageId: string; rfqPublicId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ['team', 'members'], queryFn: () => getTeamMembers(() => getToken()), staleTime: 5 * 60_000 });
  const handoffs = useQuery({ queryKey: ['copilot', 'handoffs', rfqPublicId], queryFn: () => listHandoffs(rfqPublicId, () => getToken()) });
  const [attention, setAttention] = useState<AttentionState>(proposal.attention);
  const [assignee, setAssignee] = useState<string>(proposal.assigneeMembershipId ?? '');
  const [note, setNote] = useState(proposal.noteSuggestion ?? '');
  const [includeSummary, setIncludeSummary] = useState(true);
  const save = useMutation({
    mutationFn: () => recordHandoff(rfqPublicId, {
      input: { attention, assigneeMembershipId: attention === 'needs_user' ? assignee || null : null, note: note.trim() || undefined,
        ...(includeSummary ? { summary: proposal.summary, summaryByCopilot: true } : {}) },
      expectedCurrent: handoffs.data?.handoffs.find((h) => h.current)?.id ?? null,
      // Always name the answer: it records an accepted Copilot recommendation.
      fromMessageId: messageId,
    }, () => getToken()),
    onSuccess: () => {
      for (const k of [['copilot', 'handoffs', rfqPublicId], ['copilot', 'deal-state', rfqPublicId], ['copilot', 'attention']]) void qc.invalidateQueries({ queryKey: k });
    },
    onError: () => void handoffs.refetch(),
  });
  const s = proposal.summary;
  const list = (label: string, items?: string[]) => items?.length ? (
    <div><div className="text-[11px] font-semibold text-foreground-muted">{label}</div><ul className="list-disc pl-4">{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
  ) : null;
  const members = team.data?.members ?? [];
  if (save.isSuccess) {
    const who = members.find((m) => m.membershipId === assignee);
    return <Card icon={<ArrowRightLeft className="w-3.5 h-3.5" />} title="Handoff saved"><div className="text-success">✓ {attention === 'needs_user' ? `Needs ${firstName(who?.fullName, who?.email)}` : ATTENTION_LABEL[attention as Exclude<AttentionState, 'needs_user'>]}. It now shows on the RFQ.</div></Card>;
  }
  return (
    <Card icon={<ArrowRightLeft className="w-3.5 h-3.5" />} title="Proposed handoff">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs space-y-1"><span className="text-foreground-muted">State</span>
          <select className={field} value={attention} onChange={(e) => setAttention(e.target.value as AttentionState)}>
            {ATTENTION_STATES.map((a) => <option key={a} value={a}>{a === 'needs_user' ? 'Needs a person' : ATTENTION_LABEL[a]}</option>)}
          </select>
        </label>
        {attention === 'needs_user' && (
          <label className="text-xs space-y-1"><span className="text-foreground-muted">Who</span>
            <select className={field} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Choose…</option>
              {members.filter((m) => m.role !== 'viewer').map((m) => <option key={m.membershipId} value={m.membershipId}>{m.fullName ?? m.email}{m.isSelf ? ' (you)' : ''}</option>)}
            </select>
          </label>
        )}
      </div>
      <label className="block text-xs space-y-1"><span className="text-foreground-muted">Your note (shown as written by you)</span>
        <textarea className={`${field} min-h-[64px]`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
      </label>
      <div className="rounded-md bg-surface-muted/60 p-2 space-y-1.5 text-xs">
        <label className="flex items-center gap-1.5 font-medium"><input type="checkbox" checked={includeSummary} onChange={(e) => setIncludeSummary(e.target.checked)} /> Include summary drafted by APD Copilot</label>
        {includeSummary && <div className="space-y-1.5 text-foreground">
          {s.goal && <div><span className="font-semibold">Goal:</span> {s.goal}</div>}
          {s.customerRequirement && <div><span className="font-semibold">Customer needs:</span> {s.customerRequirement}</div>}
          {s.currentBestOffer && <div><span className="font-semibold">Current best:</span> {s.currentBestOffer}</div>}
          {list('Suppliers contacted', s.suppliersContacted)}{list('Outstanding', s.outstanding)}{list('Already committed', s.commitmentsMade)}
          {list('Do not promise', s.doNotPromise)}{list('Next actions', s.nextActions)}{list('Escalate if', s.escalateIf)}
        </div>}
      </div>
      {save.isError && <div className="text-xs text-destructive">{errText(save.error)}</div>}
      <div className="flex flex-wrap gap-2">
        <button className={btnPrimary} disabled={save.isPending || (attention === 'needs_user' && !assignee) || handoffs.isLoading} onClick={() => save.mutate()}>
          <Check className="w-3.5 h-3.5" />{save.isPending ? 'Saving…' : 'Save handoff'}
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
/** A recipient Copilot found on the public web, not in APD records (charter
 *  ADR-1): where it came from, and what the server could check about it. */
function WebRecipientSource({ w, children }: { w: WebRecipient; children?: React.ReactNode }) {
  const href = safeExternalHref(w.url);
  const host = (() => { try { return new URL(w.url).hostname; } catch { return w.url; } })();
  return (
    <div className="text-[11px] rounded border border-warning/40 bg-warning-muted/40 p-1.5 space-y-0.5 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        {children}
        <span className="pill pill-warning">Not in APD</span>
        <span className="font-medium break-all">{w.email}</span>
      </div>
      <div className="text-foreground-muted min-w-0">
        Found on {href ? <a className="underline break-all" href={href} target="_blank" rel="noopener noreferrer">{w.title ?? host}</a> : host}
        {' · '}{w.domainMatchesSource ? 'same domain as the page' : 'different domain from the page'}
        {w.seenInSource ? ' · in the text Copilot quoted' : ''}
      </div>
    </div>
  );
}

export function SavedDraft({ draft, onChange }: { draft: DraftView; onChange: (d: DraftView) => void }) {
  const { getToken } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [to, setTo] = useState(draft.to.join(', '));
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [copied, setCopied] = useState(false);
  const edit = useMutation({
    mutationFn: () => editDraft(draft.id, { expectedVersion: draft.version, to: to.split(/[,;\s]+/).filter(Boolean), subject, body }, () => getToken()),
    onSuccess: (r) => { onChange(r.draft); setEditing(false); setConfirming(false); },
  });
  const toGmail = useMutation({
    // The hash is the version the person is looking at in the confirm view.
    mutationFn: () => createGmailDraft(draft.id, draft.contentSha256, () => getToken()),
    onSuccess: (r) => { onChange(r.draft); setConfirming(false); },
  });
  const discard = useMutation({ mutationFn: () => discardDraft(draft.id, () => getToken()), onSuccess: (r) => onChange(r.draft) });
  if (draft.status === 'discarded') return <div className="text-xs text-foreground-muted line-through">{draft.recipientLabel ?? draft.to.join(', ')} · removed</div>;
  const blocked = draft.issues.length > 0;
  return (
    <div className="border border-border rounded-md p-2 space-y-1.5 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-semibold min-w-0 truncate">{DRAFT_KIND_LABEL[draft.kind]} · {draft.recipientLabel ?? draft.to[0]}</div>
        {draft.status === 'gmail_draft_created'
          ? <span className="pill pill-success">In Gmail Drafts</span>
          : blocked ? <span className="pill pill-warning">Needs a fix</span> : <span className="pill pill-default">Saved</span>}
      </div>
      {draft.reason && <div className="text-[11px] text-foreground-muted">Why: {draft.reason}</div>}
      {blocked && <ul className="text-xs text-warning-foreground bg-warning-muted rounded p-1.5 list-disc pl-5">{draft.issues.map((i, j) => <li key={j}>{i.message}</li>)}</ul>}
      {editing ? (
        <div className="space-y-1.5">
          <input className={field} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
          <input className={field} value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
          <textarea className={`${field} min-h-[140px] font-mono text-xs`} value={body} onChange={(e) => setBody(e.target.value)} aria-label="Body" />
          {edit.isError && <div className="text-xs text-destructive">{errText(edit.error)}</div>}
          <div className="flex gap-2"><button className={btnPrimary} disabled={edit.isPending} onClick={() => edit.mutate()}>Save changes</button><button className={btn} onClick={() => setEditing(false)}>Cancel</button></div>
        </div>
      ) : confirming ? (
        <div className="space-y-1.5 text-xs bg-surface-muted/60 rounded p-2">
          <div className="font-semibold">Review before creating the Gmail draft</div>
          <div><span className="text-foreground-muted">From:</span> {draft.mailbox?.email}</div>
          <div><span className="text-foreground-muted">To:</span> {draft.to.join(', ')}</div>
          {draft.cc.length > 0 && <div><span className="text-foreground-muted">Cc:</span> {draft.cc.join(', ')}</div>}
          {draft.webRecipients.map((w) => <WebRecipientSource key={w.email} w={w} />)}
          <div><span className="text-foreground-muted">Subject:</span> {draft.subject}</div>
          <div><span className="text-foreground-muted">Attachments:</span> none</div>
          <pre className="whitespace-pre-wrap font-sans bg-background border border-border rounded p-2 max-h-64 overflow-auto">{draft.body}</pre>
          <div className="text-foreground-muted">This places the draft in {draft.mailbox?.email}'s Drafts folder. Nothing is sent; you send it from Gmail.</div>
          {toGmail.isError && <div className="text-destructive">{errText(toGmail.error)}</div>}
          <div className="flex gap-2 flex-wrap"><button className={btnPrimary} disabled={toGmail.isPending} onClick={() => toGmail.mutate()}><Mail className="w-3.5 h-3.5" />{toGmail.isPending ? 'Creating…' : 'Create Gmail draft'}</button><button className={btn} onClick={() => setConfirming(false)}>Back</button></div>
        </div>
      ) : (
        <>
          <div className="text-xs"><span className="text-foreground-muted">To:</span> {draft.to.join(', ')}{draft.cc.length ? ` · Cc ${draft.cc.join(', ')}` : ''}</div>
          {draft.webRecipients.map((w) => <WebRecipientSource key={w.email} w={w} />)}
          <div className="text-xs"><span className="text-foreground-muted">Subject:</span> {draft.subject}</div>
          <pre className="whitespace-pre-wrap font-sans text-xs bg-surface-muted/50 rounded p-2 max-h-40 overflow-auto">{draft.body}</pre>
          {draft.status === 'draft' && (
            <div className="flex flex-wrap gap-2">
              <button className={btnPrimary} disabled={blocked || !draft.mailbox} title={!draft.mailbox ? 'Choose a mailbox to create a Gmail draft' : undefined} onClick={() => setConfirming(true)}><Mail className="w-3.5 h-3.5" />Gmail draft…</button>
              <button className={btn} onClick={() => setEditing(true)}>Edit</button>
              <button className={btn} onClick={() => { void navigator.clipboard?.writeText(`To: ${draft.to.join(', ')}\nSubject: ${draft.subject}\n\n${draft.body}`); setCopied(true); }}><Copy className="w-3.5 h-3.5" />{copied ? 'Copied' : 'Copy'}</button>
              <button className={btn} disabled={discard.isPending} onClick={() => discard.mutate()}><Trash2 className="w-3.5 h-3.5" />Remove</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function DraftsCard({ proposal, messageId, rfqPublicId, lineItemPublicId }: { proposal: Extract<Proposal, { type: 'email_drafts' }>; messageId: string; rfqPublicId: string | null; lineItemPublicId: string | null }) {
  const { getToken } = useAuth();
  const mailboxes = useQuery({ queryKey: ['copilot', 'mailboxes'], queryFn: () => getMailboxes(() => getToken()), staleTime: 60_000 });
  const draftable = (mailboxes.data?.accessible ?? []).filter((m) => m.canDraft && m.status === 'connected');
  const [mailboxId, setMailboxId] = useState<string>(proposal.mailboxId ?? '');
  const [drafts, setDrafts] = useState(proposal.drafts.map((d, i) => ({ ...d, key: i, toText: d.to.join(', '), confirmed: [] as string[] })));
  const [saved, setSaved] = useState<DraftView[] | null>(null);
  const toList = (toText: string) => toText.split(/[,;\s]+/).filter(Boolean);
  // Web-found recipients still on each draft; every one must be ticked (charter ADR-1).
  const toConfirm = (d: (typeof drafts)[number]) => webRecipientsToConfirm({ to: toList(d.toText), cc: d.cc, webRecipients: d.webRecipients });
  const unconfirmed = drafts.reduce((n, d) => n + toConfirm(d).filter((w) => !d.confirmed.includes(w.email)).length, 0);
  const save = useMutation({
    mutationFn: () => saveDrafts({
      rfqPublicId, lineItemPublicId, mailboxId: mailboxId || null, fromMessageId: messageId,
      drafts: drafts.map(({ key: _k, toText, confirmed, webRecipients: _w, ...d }) => ({ ...d, to: toList(toText), confirmedWebRecipients: confirmed })),
    }, () => getToken()),
    onSuccess: (r) => setSaved(r.drafts),
  });
  const supplierBatch = drafts.length > 1 && drafts.every((d) => isSupplierKind(d.kind));

  if (saved) {
    return (
      <Card icon={<Mail className="w-3.5 h-3.5" />} title={`${saved.length} saved draft${saved.length === 1 ? '' : 's'}`}>
        <div className="text-xs text-foreground-muted">APD Copilot never sends email. Review each draft; "Gmail draft" places it in the mailbox's Drafts folder for you to send.</div>
        <div className="space-y-2">{saved.map((d) => <SavedDraft key={d.id} draft={d} onChange={(n) => setSaved((cur) => cur!.map((x) => (x.id === n.id ? n : x)))} />)}</div>
      </Card>
    );
  }
  return (
    <Card icon={<Mail className="w-3.5 h-3.5" />} title={supplierBatch ? `Supplier outreach · ${drafts.length} separate drafts` : `Proposed draft${drafts.length === 1 ? '' : 's'}`}>
      <label className="block text-xs space-y-1"><span className="text-foreground-muted">From</span>
        <select className={field} value={mailboxId} onChange={(e) => setMailboxId(e.target.value)}>
          <option value="">Copy only (no mailbox)</option>
          {draftable.map((m) => <option key={m.id} value={m.id}>{m.email}{m.kind === 'shared' ? ' (shared)' : ''}</option>)}
        </select>
      </label>
      {supplierBatch && <div className="text-xs text-foreground-muted">Each supplier gets its own draft with only its own address. Remove any you do not want.</div>}
      <div className="space-y-2">
        {drafts.map((d) => (
          <div key={d.key} className="border border-border rounded-md p-2 space-y-1.5 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold min-w-0 truncate">{DRAFT_KIND_LABEL[d.kind]}{d.recipientLabel ? ` · ${d.recipientLabel}` : ''}</div>
              <button className="p-1 text-foreground-muted hover:text-destructive" aria-label="Remove this draft" onClick={() => setDrafts((cur) => cur.filter((x) => x.key !== d.key))}><X className="w-3.5 h-3.5" /></button>
            </div>
            {d.reason && <div className="text-[11px] text-foreground-muted">Why: {d.reason}</div>}
            <input className={field} aria-label="To" placeholder="To" value={d.toText} onChange={(e) => setDrafts((cur) => cur.map((x) => (x.key === d.key ? { ...x, toText: e.target.value } : x)))} />
            {toConfirm(d).map((w) => (
              <WebRecipientSource key={w.email} w={w}>
                <input type="checkbox" aria-label={`I checked ${w.email} and its source`} checked={d.confirmed.includes(w.email)}
                  onChange={(e) => setDrafts((cur) => cur.map((x) => (x.key === d.key
                    ? { ...x, confirmed: e.target.checked ? [...x.confirmed, w.email] : x.confirmed.filter((c) => c !== w.email) } : x)))} />
              </WebRecipientSource>
            ))}
            <input className={field} aria-label="Subject" placeholder="Subject" value={d.subject} onChange={(e) => setDrafts((cur) => cur.map((x) => (x.key === d.key ? { ...x, subject: e.target.value } : x)))} />
            <textarea className={`${field} min-h-[120px] text-xs`} aria-label="Body" value={d.body} onChange={(e) => setDrafts((cur) => cur.map((x) => (x.key === d.key ? { ...x, body: e.target.value } : x)))} />
          </div>
        ))}
      </div>
      {save.isError && <div className="text-xs text-destructive">{errText(save.error)}</div>}
      <div className="flex flex-wrap gap-2 items-center">
        <button className={btnPrimary} disabled={!drafts.length || unconfirmed > 0 || save.isPending} onClick={() => save.mutate()}><Check className="w-3.5 h-3.5" />{save.isPending ? 'Saving…' : `Save ${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}</button>
        <span className="text-[11px] text-foreground-muted">{unconfirmed > 0
          ? `Tick ${unconfirmed} address${unconfirmed === 1 ? '' : 'es'} found on the web after checking ${unconfirmed === 1 ? 'its' : 'their'} source.`
          : 'Saving checks recipients. Nothing is sent.'}</span>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
export function CandidateCard({ proposal, index, messageId, citations }: { proposal: Extract<Proposal, { type: 'relationship_candidate' }>; index: number; messageId: string; citations: Citation[] }) {
  const { getToken } = useAuth();
  const [saved, setSaved] = useState<CandidateView | null>(null);
  const record = useMutation({ mutationFn: () => recordCandidateFromMessage(messageId, index, () => getToken()), onSuccess: (r) => setSaved(r.candidate) });
  const review = useMutation({ mutationFn: (v: 'confirmed' | 'rejected') => reviewCandidate(saved!.id, v, undefined, () => getToken()), onSuccess: (r) => setSaved(r.candidate) });
  const evidence = proposal.evidence.map((id) => citations.find((c) => c.id === id)).filter(Boolean) as Citation[];
  return (
    <Card icon={<FileSearch className="w-3.5 h-3.5" />} title="Research candidate">
      <div className="text-sm"><span className="font-mono">{proposal.subjectIdentifier}</span> → <span className="font-mono font-semibold">{proposal.proposedValue}</span></div>
      <div className="flex flex-wrap gap-1.5 text-xs"><span className="pill pill-info">{RELATIONSHIP_LABEL[proposal.relationship]}</span><span className="pill pill-default">Evidence grade {proposal.grade}</span>
        {saved && <span className={`pill ${saved.status === 'confirmed' ? 'pill-success' : saved.status === 'rejected' ? 'pill-danger' : 'pill-warning'}`}>{saved.status === 'proposed' ? 'Awaiting broker' : saved.status}</span>}</div>
      <div className="text-xs">{proposal.rationale}</div>
      {evidence.length > 0 && <ul className="text-xs list-disc pl-4 text-foreground-muted">{evidence.map((c) => <li key={c.id}>{c.label}</li>)}</ul>}
      <div className="text-[11px] text-foreground-muted">A candidate is not APD knowledge until a broker confirms it. Confirming records acceptance for APD's purposes; it is not an approved substitute or an airworthiness statement. The customer's part number is never changed.</div>
      {(record.isError || review.isError) && <div className="text-xs text-destructive">{errText(record.error ?? review.error)}</div>}
      <div className="flex flex-wrap gap-2">
        {!saved && <button className={btnPrimary} disabled={record.isPending} onClick={() => record.mutate()}>Save as candidate</button>}
        {saved?.status === 'proposed' && <>
          <button className={btnPrimary} disabled={review.isPending} onClick={() => review.mutate('confirmed')}><Check className="w-3.5 h-3.5" />Confirm</button>
          <button className={btn} disabled={review.isPending} onClick={() => review.mutate('rejected')}><X className="w-3.5 h-3.5" />Reject</button>
        </>}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
export function PartsBaseSearchCard({ proposal, rfqPublicId, onAskAbout }: { proposal: Extract<Proposal, { type: 'partsbase_search' }>; rfqPublicId: string; onAskAbout: (text: string) => void }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);
  const run = useMutation({
    // Same query key as the RFQ page's PartsBase panel, so the results appear there too.
    mutationFn: () => qc.fetchQuery({ queryKey: ['partsbase-listings', proposal.partNumber.trim().toUpperCase()], queryFn: () => runPartsBaseSearch(rfqPublicId, proposal.lineItemPublicId, () => getToken()), staleTime: 5 * 60_000 }),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ['part-intelligence', rfqPublicId] }), 750),
  });
  const r = run.data;
  const summary = useMemo(() => {
    if (!r) return null;
    if (r.status !== 'ok') return r.message;
    return `${r.listings.length} exact listing${r.listings.length === 1 ? '' : 's'} from ${r.sellerCount} seller${r.sellerCount === 1 ? '' : 's'}${r.cached ? ` (stored result, ${r.cacheAgeMinutes ?? 0} min old)` : ' (live search)'}`;
  }, [r]);
  return (
    <Card icon={<Radar className="w-3.5 h-3.5" />} title="Live PartsBase search" tone="warn">
      <div className="text-sm">Search PartsBase for <span className="font-mono font-semibold">{proposal.partNumber}</span></div>
      <div className="text-xs text-foreground-muted">{proposal.reason}</div>
      <div className="flex items-start gap-1.5 text-[11px] text-foreground-muted"><ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />A live search is a metered PartsBase call. It runs only if you press the button; a fresh stored result is reused instead.</div>
      {run.isError && <div className="text-xs text-destructive">{errText(run.error)}</div>}
      {summary ? (
        <div className="space-y-1.5"><div className="text-xs font-medium">{summary}</div>
          {r?.status === 'ok' && <button className={btn} onClick={() => onAskAbout(`I ran the PartsBase search for ${proposal.partNumber}. Review the stored results and suggest who to contact.`)}>Ask Copilot to review the results</button>}
        </div>
      ) : armed ? (
        <div className="flex gap-2 flex-wrap"><button className={btnPrimary} disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? 'Searching…' : 'Yes, run the search'}</button><button className={btn} onClick={() => setArmed(false)}>Cancel</button></div>
      ) : (
        <button className={btn} onClick={() => setArmed(true)}><Radar className="w-3.5 h-3.5" />Run PartsBase search…</button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
/** Recorded candidates for an identifier, with the broker's review buttons. */
export function CandidateList({ identifier }: { identifier: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['copilot', 'candidates', identifier.toUpperCase()], queryFn: () => listCandidates(identifier, () => getToken()), staleTime: 60_000 });
  const review = useMutation({
    mutationFn: (a: { id: string; v: 'confirmed' | 'rejected' | 'withdrawn' }) => reviewCandidate(a.id, a.v, undefined, () => getToken()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot', 'candidates', identifier.toUpperCase()] }),
  });
  const items = (q.data?.candidates ?? []).filter((c) => c.status !== 'withdrawn');
  if (!items.length) return null;
  return (
    <section className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted">Research candidates</div>
      <ul className="space-y-1.5">
        {items.map((c) => (
          <li key={c.id} className="text-xs flex flex-wrap items-center gap-1.5">
            <span className="font-mono">{c.proposedValue}</span>
            <span className="pill pill-info">{RELATIONSHIP_LABEL[c.relationship]}</span>
            <span className="pill pill-default">Grade {c.grade}</span>
            <span className={`pill ${c.status === 'confirmed' ? 'pill-success' : c.status === 'rejected' ? 'pill-danger' : 'pill-warning'}`}>{c.status === 'proposed' ? 'Awaiting broker' : c.status === 'confirmed' ? `Confirmed by ${c.by ?? 'a broker'}` : c.status}</span>
            {c.status === 'proposed' && <>
              <button className={btn} disabled={review.isPending} onClick={() => review.mutate({ id: c.id, v: 'confirmed' })}>Confirm</button>
              <button className={btn} disabled={review.isPending} onClick={() => review.mutate({ id: c.id, v: 'rejected' })}>Reject</button>
            </>}
            {c.status === 'confirmed' && <button className={btn} disabled={review.isPending} onClick={() => review.mutate({ id: c.id, v: 'withdrawn' })}>Withdraw</button>}
          </li>
        ))}
      </ul>
      {review.isError && <div className="text-xs text-destructive">{errText(review.error)}</div>}
    </section>
  );
}
